import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext, useParams, useNavigate, useMatch } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';

import RunStatusList from './Runs/RunStatusList';
import AddSamplesDialog from './Samples/AddSamplesDialog';
import AddRunDialog from './Runs/AddRunDialog';
import NCBIUploadDialog from './NCBI/NCBIUploadDialog';
import ClonalityAnalysisDialog from './Clonality/ClonalityAnalysisDialog';
import ClonalityAnalysisView from './Clonality/ClonalityAnalysisView';
import { useProjectAnalysisStatus } from './AnalysisStatus/useProjectAnalysisStatus';

import DatasetsDataGrid from './Datasets/DatasetsDataGrid.jsx';
import { useDatasets, useClonalityAnalysisExecutions } from './services/api/KeynomeAPIHooks.js';
import { useRecordDatasetViewed } from './services/api/KeynomeAPIMutations.js';

const RECENTLY_VIEWED_DATASET_CARD_COUNT = 3;

function Project() {
  const {
    activeProject,
    datasetsGridRef,
    uploadFile,
    uploadingFilesCt,
    userData,
    setUserErrorMessage,
    setProgressMessage,
    hasAccessNCBIUpload,
    hasAccessClonalityAnalysis,
    organization,
    isProjectSelectionReady
  } = useOutletContext();
  const params = useParams();
  const navigate = useNavigate();
  const clonalityRouteMatch = useMatch('/projects/:projectId/clonality');

  const [ activeDataset, setActiveDataset ] = useState(null);
  const [ addDatasetDialogOpen, setAddDatasetDialogOpen ] = useState(false);
  const [ ncbiUploadDialogOpen, setNCBIUploadDialogOpen ] = useState(false);
  const [ addSamplesDialogOpen, setAddSamplesDialogOpen ] = useState(false);
  const [ clonalityDialogOpen, setClonalityDialogOpen ] = useState(false);

  const { data: datasets, refetch: refetchDatasets } = useDatasets(activeProject?.id, { queryParams: { extraFields: ['keynome_web_url'] } });
  const { data: clonalityExecutions } = useClonalityAnalysisExecutions(
    hasAccessClonalityAnalysis ? organization?.id : null
  );
  const projectAnalysisStatus = useProjectAnalysisStatus({
    projectId: activeProject?.id,
    enabled: Boolean(activeProject && organization && hasAccessClonalityAnalysis && clonalityDialogOpen),
  });
  const hasEligibleClonalityExecutions = (clonalityExecutions || []).some((execution) => (
    execution.attributes.is_active && execution.attributes.status === 'succeeded'
  ));

  const lastRecordedDatasetUuidRef = useRef(null);

  const { mutateAsync: recordDatasetViewed } = useRecordDatasetViewed();

  useEffect(() => {
    if (!datasets) return;

    const dataset = datasets.find((dataset) => dataset.attributes.uuid === params.runId);

    if (dataset) {
      setActiveDataset(dataset);
      setAddSamplesDialogOpen(true);
    } else if (!params.runId) {
      // Clear only when no runId is present; otherwise keep current activeDataset
      setActiveDataset(null);
    }
    // If params.runId exists but dataset not found yet (e.g., refetch race), keep existing activeDataset
  }, [datasets, params.runId]);

  useEffect(() => {
    const datasetUuid = activeDataset?.attributes?.uuid;
    if (!datasetUuid) return;
    if (lastRecordedDatasetUuidRef.current === datasetUuid) return;

    lastRecordedDatasetUuidRef.current = datasetUuid;

    recordDatasetViewed({ datasetId: datasetUuid }).catch((error) => {
      console.error(error);
    });
  }, [activeDataset?.attributes?.uuid, recordDatasetViewed]);

  const recentlyViewedDatasets = useMemo(() => {
    const recentlyViewedRunUuids = userData?.data?.attributes?.recently_viewed_runs || [];
    if (!datasets) return Array(RECENTLY_VIEWED_DATASET_CARD_COUNT).fill(null);

    const datasetsByUuid = new Map(
      datasets.map((dataset) => [dataset.attributes.uuid, dataset])
    );

    return recentlyViewedRunUuids
      .map((uuid) => datasetsByUuid.get(uuid))
      .filter(Boolean)
      .concat(Array(RECENTLY_VIEWED_DATASET_CARD_COUNT).fill(1))
      .slice(0, RECENTLY_VIEWED_DATASET_CARD_COUNT);
  }, [datasets, userData?.data?.attributes?.recently_viewed_runs]);

  useEffect(() => {
    if (!hasAccessClonalityAnalysis) {
      setClonalityDialogOpen(false);
      return;
    }
    setClonalityDialogOpen(Boolean(clonalityRouteMatch));
  }, [clonalityRouteMatch, hasAccessClonalityAnalysis]);

  if (!isProjectSelectionReady) {
    return (
      <Grid container maxWidth="lg" spacing={2} sx={{ m: 'auto', mt: 1, paddingRight: '30px' }}>
        <Grid item xs={12} sx={{ marginTop: '50px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (!activeProject) {
    return (
      <Grid container maxWidth="lg" spacing={2} sx={{ m: 'auto', mt: 1, paddingRight: '30px' }}>
        <Grid item xs={12} sx={{ marginTop: '50px' }}>
          <Box sx={{ textAlign: 'center', color: '#797979', py: 8 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No active project selected
            </Typography>
            <Typography variant="body2">
              Create or restore a project from Manage Projects to continue.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  const recentlyViewedDatasetsContent = recentlyViewedDatasets ? 
    (
      <RunStatusList
        runs={recentlyViewedDatasets}
        activeRun={activeDataset}
        setActiveRun={setActiveDataset}
        setAddSamplesDialogOpen={setAddSamplesDialogOpen}
      />
    ) : null;

  const createNewDatasetButton = activeDataset ?
    null : (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.25,
          mt: '30px'
        }}
      >
        {hasAccessNCBIUpload && (
          <Button
            size="large"
            sx={{
              height: 44,
              backgroundColor: '#20558a',
              color: '#fff',
              '& .MuiButton-startIcon': {
                marginLeft: 0,
                marginRight: '4px'
              },
              '&:hover': {
                backgroundColor: '#184269'
              },
              padding: "10px 20px 10px 10px",
              minWidth: "190px"
            }}
            variant="contained"
            aria-label="analyze-from-ncbi"
            data-cy="analyze-from-ncbi"
            onClick={() => setNCBIUploadDialogOpen(true)}
            startIcon={
              <Box
                component="img"
                src="/ncbi_logo.png"
                alt="NCBI logo"
                sx={{ width: 35, height: 35, objectFit: 'contain' }}
              />
            }
          >
            Analyze NCBI
          </Button>
        )}
        <Button
          size="large"
          sx={{ marginRight: '10px', height: 44, minWidth: '200px', padding: "5px" }}
          variant="contained"
          color="info"
          aria-label="create-dataset"
          data-cy="create-new-dataset"
          onClick={() => setAddDatasetDialogOpen(true)}
          startIcon={<AddIcon />}
        >
          Create Dataset
        </Button>
        {hasAccessClonalityAnalysis && (
          hasEligibleClonalityExecutions ? (
            <Button
              size="large"
              sx={{ marginRight: '10px', height: 44, minWidth: '220px', padding: "5px" }}
              variant="contained"
              color="secondary"
              aria-label="open-clonality-analysis"
              data-cy="open-clonality-analysis"
              onClick={() => navigate(`/projects/${activeProject.attributes.uuid}/clonality`)}
            >
              Clonality Analysis
            </Button>
          ) : (
            <Tooltip title="No completed clonality analyses are available. Results will appear after analysis completes.">
              <span>
                <Button
                  size="large"
                  sx={{ marginRight: '10px', height: 44, minWidth: '220px', padding: "5px" }}
                  variant="contained"
                  color="secondary"
                  aria-label="open-clonality-analysis"
                  data-cy="open-clonality-analysis"
                  disabled
                >
                  Clonality Analysis
                </Button>
              </span>
            </Tooltip>
          )
        )}
      </Box>
    )      

  return (
    <Grid container maxWidth="lg" spacing={2} sx={{ m: 'auto', mt: 1, paddingRight: '30px' }}>
      <Grid item xs={12} sx={{marginTop: '50px'}}>
        <p style={{color: '#797979', marginBottom: '2px', marginTop: '2px'}}>
          <b>RECENTLY VIEWED DATASETS</b>
        </p>
      </Grid>
      {recentlyViewedDatasetsContent}
      <Grid item xs={12} sm={8} sx={{ marginTop: '50px'}}>
        <p style={{color: '#797979', marginBottom: '2px', marginTop: '2px'}}>
          <b>SEQUENCING DATASETS</b>
        </p>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box sx={{ textAlign: 'right'}}>
          {createNewDatasetButton}
        </Box>
      </Grid>
      <Grid 
        item 
        xs={12} 
        height="665px" 
        sx={{
          '& .keynome-table--header': { backgroundColor: "#eee" },
          marginBottom: '40px'
        }}
      >
        <DatasetsDataGrid
          datasets={datasets}
          datasetsGridRef={datasetsGridRef}
          setAddSamplesDialogOpen={setAddSamplesDialogOpen}
        />
      </Grid>
      <AddSamplesDialog 
        key={activeProject?.id}
        open={addSamplesDialogOpen} 
        setOpen={setAddSamplesDialogOpen} 
        activeRun={activeDataset} 
        uploadFile={uploadFile} 
        backgroundUploadingFilesCt={uploadingFilesCt}  
        activeProject={activeProject} 
        setReloadRuns={refetchDatasets}
      />
      {hasAccessClonalityAnalysis && (
        <ClonalityAnalysisDialog
          open={clonalityDialogOpen}
          onClose={() => navigate(`/projects/${activeProject.attributes.uuid}`)}
          executions={clonalityExecutions || []}
          analysisStatus={projectAnalysisStatus}
        >
          <ClonalityAnalysisView />
        </ClonalityAnalysisDialog>
      )}
      <AddRunDialog 
        open={addDatasetDialogOpen} 
        setOpen={setAddDatasetDialogOpen} 
        activeProject={activeProject} 
        userData={userData} 
        reloadRuns={refetchDatasets} 
        setUserErrorMessage={setUserErrorMessage}
        setAddSamplesDialogOpen={setAddSamplesDialogOpen}
        setActiveDataset={setActiveDataset}
      />
      <NCBIUploadDialog
        open={ncbiUploadDialogOpen}
        setOpen={setNCBIUploadDialogOpen}
        activeProject={activeProject}
        reloadRuns={refetchDatasets}
        setUserErrorMessage={setUserErrorMessage}
        setProgressMessage={setProgressMessage}
      />
    </Grid>
  );
}

export default Project;
